import type {Iso4217, Iso8601} from '@hconnect/common/types'
import {Moment} from 'moment-timezone'

import {Priority} from '../../types/base.types'
import {ShiftEventCategory, StoppageKind} from '../../types/category.types'
import {EquipmentData} from '../../types/equipment.types'

import {InforWorkOrder} from './task.types'

export type StoppageTypeWithReason =
  | 'cementMill'
  | 'rawMill'
  | 'coalMill'
  | 'crusher'
  | 'rollerPress'
  | 'verticalMill'
  | 'ballMill'
  | 'dosingEquipment'
  | 'bypass'
  | 'other'
  | 'alternativeFuelInstallation'

export type StoppageTypeWithCode = 'kiln' | 'cementMill' | 'rawMill' | 'alternativeFuelInstallation'
export type StoppageType = 'kiln' | StoppageTypeWithReason

export type StoppageDetails =
  | {
      stoppageType: StoppageTypeWithCode
      stoppageCode: string
    }
  | {
      stoppageType: StoppageTypeWithReason
      stoppageReason: string
    }

export enum AnalysisType {
  Custom = 0,
  Cockpit = 1
}

export enum AnalysisStatus {
  Ongoing = 'Ongoing',
  Finished = 'Finished'
}

export enum AnalysisPrivacyStatus {
  Private = 'Private',
  Public = 'Public'
}

export type RootCauseFailureAnalyses = {
  id: number
  title: string
  externalId: string
  plantId: string
  status: AnalysisStatus
  type: AnalysisType
  privacyStatus: AnalysisPrivacyStatus
  ownerId: string
  contributorIds: string[]
}

export enum RunningTimeType {
  RUNNING = 'running',
  STOPPED = 'stopped'
}

type RunningTimeStoppageBase<TDate> = {
  stoppageKind: StoppageKind
  start: TDate
  end?: TDate
  stoppageType?: StoppageType
  cockpitStoppageId?: string
  stoppageCode?: string
  stoppageReason?: string
}

export type RunningTimeStoppageDto = RunningTimeStoppageBase<Iso8601>

export type RunningTimeStoppage = RunningTimeStoppageBase<Moment>

type RunningTimeBase<TDate, TStoppage> = {
  id?: number
  beginTechnical?: TDate
  endTechnical?: TDate
  begin?: TDate
  end?: TDate
  runningTimeType?: RunningTimeType
  stoppageType?: StoppageType
  stoppageKind?: StoppageKind
  rootCauseFailureAnalyses?: RootCauseFailureAnalyses[]
  stoppages?: TStoppage[]
  stoppageGroup?: string
}

export type RunningTimeDto = RunningTimeBase<Iso8601, RunningTimeStoppageDto> & {
  stoppageCode?: string
  stoppageReason?: string
  cockpitStoppageId?: string
}

export type RunningTime = RunningTimeBase<Moment, RunningTimeStoppage>

type StoppageBase<TDate, TRunningTime> = StoppageDetails & {
  id: string
  title: string
  eventType: StoppageKind
  equipment?: EquipmentData
  mainEquipment?: EquipmentData
  status: 'none'
  commentCount: number
  isSubmitted: boolean

  stoppageStart: TDate
  stoppageEnd?: TDate

  isHighlighted?: boolean
  hasSalesImpact?: boolean
  cost?: number
  costCurrency?: Iso4217
  description: string | null
  processStage?: string
  priority?: Priority
  category?: ShiftEventCategory
  pxTrendTag?: string
  pxTrendStoppage?: TRunningTime
  rootCauseFailureAnalyses?: RootCauseFailureAnalyses[]
  inforWorkOrder?: InforWorkOrder
  originalSplitStoppageId?: string
}

export type StoppageDto = StoppageBase<Iso8601, RunningTimeDto>

export type Stoppage = StoppageBase<Moment, RunningTime> & {
  pxtrendStoppageId?: number
  stoppageGroup?: string
  upmDepartmentId?: string
}
